import { useEffect, useState } from "react";
import { isPingfloTourRunning } from "../tour/tourTarget";
import { useTourDemoActive } from "../tour/useTourDemoActive";

const IMMERSIVE_CLASS = "lt-immersive";
const PLAYER_FORCE_CLASS = "lt-player-live";
const IOS_PHONE_CLASS = "lt-ios-phone";

const DEFAULT_SKIP =
  'input, textarea, select, option, button, a, label, [contenteditable="true"], .lt-fb-sheet, .lt-fb-sheet-portal, .lt-fb-voice-hold, .lt-sheet, .lt-sheet-backdrop, .lt-plan-sheet-backdrop, .MuiModal-root, .MuiDialog-root, .pingflo-tour-overlay, .cm-dock-dim, .cm-cam-settings, .cm-fs-gate';
/* Skip form chrome and sheets so typing / scrolling settings does not steal
   the gesture; empty canvas taps and the fullscreen gate still enter FS. */
const FORCE_SKIP =
  'input, textarea, select, option, [contenteditable="true"], .MuiModal-root, .MuiDialog-root, .lt-settings-dialog, .lt-fb-sheet, .lt-sheet, .lt-sheet-backdrop, .lt-fb-sheet-portal, .pingflo-tour-overlay, .lt-fs-gate, .cm-fs-gate';

export function isFullscreenActive() {
  return !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  );
}

/**
 * Portal root for Live Training overlays (tables dialog, setup wizard, sheets).
 * Fullscreen prefers `.lt-page` (see fullscreenTargets); nodes under document.body
 * sit outside that subtree and are invisible while `.lt-page` is fullscreen.
 */
export function getLiveTrainingPortalRoot() {
  if (typeof document === "undefined") return null;
  const fs =
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement;
  if (fs instanceof Element) {
    if (fs.classList?.contains("lt-page")) return fs;
    if (fs === document.documentElement || fs === document.body) {
      return document.querySelector(".lt-page") || document.body;
    }
    return fs;
  }
  return document.querySelector(".lt-page") || document.body;
}

/** Above the plan sheet (4200) so MUI dialogs stay visible in fullscreen `.lt-page`. */
export const LIVE_TRAINING_DIALOG_Z_INDEX = 5000;

/**
 * Re-reads the portal root when fullscreen starts or stops so MUI Dialogs
 * stay inside `.lt-page` instead of vanishing on `document.body`.
 */
export function useLiveTrainingPortalRoot() {
  const [root, setRoot] = useState(() => getLiveTrainingPortalRoot());
  useEffect(() => {
    const sync = () => setRoot(getLiveTrainingPortalRoot());
    sync();
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);
  return root;
}

export function canRequestPageFullscreen() {
  if (typeof document === "undefined") return false;
  const el = document.documentElement;
  return !!(
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.mozRequestFullScreen ||
    el.msRequestFullscreen
  );
}

const FS_GATE_STORAGE_KEY = "lt-fs-gate-dismissed";

/** iPhone/iPod, including Chrome/Firefox/Edge on iOS (all WebKit). */
export function isIosPhoneBrowser() {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPod/i.test(navigator.userAgent || "");
}

/**
 * iOS WebKit (Safari, Chrome, Firefox, Edge) and iPad desktop-mode.
 * Document fullscreen is rejected; the toolbar hides by scrolling, and
 * element fullscreen must target a page node rather than `<html>`.
 */
export function isAppleTouchBrowser() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPhone|iPod|iPad|CriOS|FxiOS|EdgiOS/i.test(ua)) return true;
  const platform = navigator.platform || "";
  return platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
}

/** True only when html/body fullscreen is likely to succeed (not iPhone Safari/WebKit). */
export function pageFullscreenLikelyWorks() {
  return canRequestPageFullscreen() && !isIosPhoneBrowser();
}

export function isLiveFullscreenGateDismissed() {
  try {
    return sessionStorage.getItem(FS_GATE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function dismissLiveFullscreenGate() {
  try {
    sessionStorage.setItem(FS_GATE_STORAGE_KEY, "1");
  } catch {
    /* private mode */
  }
}

function asRequestPromise(result) {
  if (result && typeof result.then === "function") {
    return result.catch(() => {});
  }
  return Promise.resolve();
}

function invokeFullscreen(el) {
  if (!el) return null;
  const standard = el.requestFullscreen?.bind(el);
  const legacy =
    el.webkitRequestFullscreen?.bind(el) ||
    el.mozRequestFullScreen?.bind(el) ||
    el.msRequestFullscreen?.bind(el) ||
    null;
  // No FullscreenOptions. `navigationUI: "hide"` returns a rejected promise
  // on several mobile Chrome/Safari builds, and that rejection spends the
  // user gesture so the plain retry never runs.
  if (standard) {
    try {
      const result = standard();
      if (result != null) return result;
    } catch {
      /* try the prefixed entry point */
    }
  }
  if (legacy) {
    try {
      return legacy();
    } catch {
      return null;
    }
  }
  return null;
}

function fullscreenTargets(source) {
  const targets = [];
  const add = (el) => {
    if (!el || targets.includes(el)) return;
    targets.push(el);
  };
  const fromEvent =
    source?.currentTarget instanceof Element ? source.currentTarget : null;
  const fromNode = source instanceof Element ? source : fromEvent;
  add(fromNode?.closest?.(".lt-page"));
  add(fromNode?.closest?.(".cm-page"));
  if (typeof document !== "undefined") {
    add(document.querySelector(".lt-page"));
    add(document.querySelector(".cm-page"));
    add(document.body);
    add(document.documentElement);
  }
  return targets;
}

/**
 * Player live training: one element per gesture.
 * Android Chrome hides the toolbar when `<html>` goes fullscreen.
 * iOS rejects `<html>` / `<body>` and only accepts a descendant (the page).
 */
function browserChromeTargets() {
  const targets = [];
  const add = (el) => {
    if (!el || targets.includes(el)) return;
    targets.push(el);
  };
  if (typeof document === "undefined") return targets;
  const page =
    document.querySelector(".lt-page") || document.querySelector(".cm-page");
  if (isAppleTouchBrowser()) {
    add(page);
    add(document.documentElement);
    add(document.body);
  } else {
    add(document.documentElement);
    add(document.body);
    add(page);
  }
  return targets;
}

/**
 * Immersive CSS sets position:fixed on html before the request. Chrome and
 * Safari reject Fullscreen API on a fixed document, so the toolbar stays.
 * Override that for the request itself; restore once the call has been issued.
 */
function relaxFixedForFullscreen() {
  const nodes = [
    document.documentElement,
    document.body,
    document.querySelector(".lt-page"),
    document.querySelector(".cm-page"),
  ].filter(Boolean);
  const saved = nodes.map((el) => [el, el.getAttribute("style")]);
  for (const el of nodes) {
    // Normal inline style beats the immersive `position: fixed` rule, and
    // still loses to `:fullscreen { position: fixed !important }` once the
    // browser actually enters fullscreen. `!important` here would stick and
    // keep the toolbar visible.
    el.style.setProperty("position", "static");
    el.style.setProperty("top", "0");
  }
  return () => {
    for (const [el, prev] of saved) {
      if (prev == null) el.removeAttribute("style");
      else el.setAttribute("style", prev);
    }
  };
}

function pinFullscreenViewport() {
  const root = document.documentElement;
  const h = window.innerHeight || root.clientHeight;
  const w = window.innerWidth || root.clientWidth;
  root.style.setProperty("--lt-vvt", "0px");
  root.style.setProperty("--lt-vvl", "0px");
  if (h) root.style.setProperty("--lt-vvh", `${Math.round(h)}px`);
  if (w) root.style.setProperty("--lt-vvw", `${Math.round(w)}px`);
}

/**
 * Must run in the same turn as a user gesture (touchend or click). Do not
 * await before the first request — that drops mobile user-activation.
 * Coach overlays still prefer `.lt-page`. Player live training passes
 * hideBrowserChrome so the browser bar hides.
 */
let pendingFullscreen = null;

/** Fullscreen one element (default: the page) and ask the browser to hide its bar. */
export function requestElementFullscreen(el) {
  if (typeof document === "undefined") return Promise.resolve();
  const target = el instanceof Element ? el : document.documentElement;
  const current =
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement;
  if (current === target) return Promise.resolve();
  return asRequestPromise(invokeFullscreen(target));
}

export function requestLiveFullscreen(source, options = {}) {
  if (typeof document === "undefined") return Promise.resolve();
  if (isPingfloTourRunning()) return Promise.resolve();
  const hideBrowserChrome = Boolean(options.hideBrowserChrome);
  if (isFullscreenActive()) {
    if (hideBrowserChrome) pinFullscreenViewport();
    return Promise.resolve();
  }
  // A second call in the same tap aborts the first. Mobile browsers then
  // reject both, the toolbar stays, and the gate button never goes away.
  if (pendingFullscreen) return pendingFullscreen;

  const targets = hideBrowserChrome ? browserChromeTargets() : fullscreenTargets(source);
  const restore = hideBrowserChrome ? relaxFixedForFullscreen() : null;

  const finish = () => {
    restore?.();
    if (isFullscreenActive()) {
      if (hideBrowserChrome) pinFullscreenViewport();
    } else if (hideBrowserChrome && isAppleTouchBrowser()) {
      nudgeAppleBrowserChrome();
      syncVisualViewportHeight();
    } else {
      syncVisualViewportHeight();
    }
  };

  let result = null;
  for (const el of targets) {
    result = invokeFullscreen(el);
    if (isFullscreenActive()) {
      finish();
      return asRequestPromise(result);
    }
    // A promise is in flight. Another target now would abort it, and the
    // retry would run outside this user gesture.
    if (result != null) break;
  }

  if (hideBrowserChrome && isAppleTouchBrowser()) {
    nudgeAppleBrowserChrome();
  }

  if (result && typeof result.then === "function") {
    const tracked = result.then(
      () => {
        finish();
      },
      () => {
        finish();
      }
    );
    pendingFullscreen = tracked;
    tracked.finally(() => {
      if (pendingFullscreen === tracked) pendingFullscreen = null;
    });
    return tracked;
  }

  finish();
  return Promise.resolve();
}

export function exitLiveFullscreen() {
  if (!isFullscreenActive()) return Promise.resolve();
  const exit =
    document.exitFullscreen?.bind(document) ||
    document.webkitExitFullscreen?.bind(document) ||
    document.mozCancelFullScreen?.bind(document) ||
    document.msExitFullscreen?.bind(document) ||
    null;
  return exit?.()?.catch(() => {}) || Promise.resolve();
}

/** Scroll the document 1px so iOS Safari / iOS Chrome collapse the URL bar. */
function nudgeAppleBrowserChrome() {
  if (typeof window === "undefined" || !isAppleTouchBrowser()) return;
  try {
    if ((window.scrollY || window.pageYOffset || 0) < 1) {
      window.scrollTo(0, 1);
    }
  } catch {
    /* ignore */
  }
}

/** Collapse Safari / iOS Chrome URL bar as far as the browser allows. */
export function minimizeMobileBrowserChrome() {
  if (typeof window === "undefined") return;
  syncVisualViewportHeight();
  nudgeAppleBrowserChrome();
}

/** Publish visualViewport box as CSS vars so live UI fits under the browser bar. */
export function syncVisualViewportHeight() {
  if (typeof window === "undefined") return;
  const vv = window.visualViewport;
  // Prefer the shortest reported height. Some Android OEMs (e.g. Oppo ColorOS)
  // keep the toolbar up and leave one of vv/innerHeight at the full layout size
  // while the other tracks the reduced visible area.
  const heights = [
    vv?.height,
    window.innerHeight,
    document.documentElement?.clientHeight,
  ].filter((n) => typeof n === "number" && Number.isFinite(n) && n > 0);
  const widths = [
    vv?.width,
    window.innerWidth,
    document.documentElement?.clientWidth,
  ].filter((n) => typeof n === "number" && Number.isFinite(n) && n > 0);
  const h = heights.length ? Math.min(...heights) : 0;
  const w = widths.length ? Math.min(...widths) : 0;
  const top = vv?.offsetTop || 0;
  const left = vv?.offsetLeft || 0;
  const root = document.documentElement;
  if (h) root.style.setProperty("--lt-vvh", `${Math.round(h)}px`);
  if (w) root.style.setProperty("--lt-vvw", `${Math.round(w)}px`);
  root.style.setProperty("--lt-vvt", `${Math.round(top)}px`);
  root.style.setProperty("--lt-vvl", `${Math.round(left)}px`);
}

function setImmersiveClass(on, forcePlayer) {
  const root = document.documentElement;
  const body = document.body;
  if (on) {
    root.classList.add(IMMERSIVE_CLASS);
    body?.classList.add(IMMERSIVE_CLASS);
    if (forcePlayer) {
      root.classList.add(PLAYER_FORCE_CLASS);
      body?.classList.add(PLAYER_FORCE_CLASS);
      if (isAppleTouchBrowser()) {
        root.classList.add(IOS_PHONE_CLASS);
        body?.classList.add(IOS_PHONE_CLASS);
      }
    }
  } else {
    root.classList.remove(IMMERSIVE_CLASS, PLAYER_FORCE_CLASS, IOS_PHONE_CLASS);
    body?.classList.remove(IMMERSIVE_CLASS, PLAYER_FORCE_CLASS, IOS_PHONE_CLASS);
    root.style.removeProperty("--lt-vvh");
    root.style.removeProperty("--lt-vvw");
    root.style.removeProperty("--lt-vvt");
    root.style.removeProperty("--lt-vvl");
  }
}

/** Survive React effect re-runs without cancelling a join-tap fullscreen. */
let immersiveOwnerCount = 0;
let immersiveExitTimer = 0;

function acquireImmersive(forcePlayer) {
  immersiveOwnerCount += 1;
  if (typeof window !== "undefined") {
    window.clearTimeout(immersiveExitTimer);
  }
  setImmersiveClass(true, forcePlayer);
}

function releaseImmersive() {
  immersiveOwnerCount = Math.max(0, immersiveOwnerCount - 1);
  if (typeof window === "undefined") {
    if (immersiveOwnerCount <= 0) {
      setImmersiveClass(false, false);
      exitLiveFullscreen();
    }
    return;
  }
  window.clearTimeout(immersiveExitTimer);
  immersiveExitTimer = window.setTimeout(() => {
    if (immersiveOwnerCount > 0) return;
    setImmersiveClass(false, false);
    exitLiveFullscreen();
  }, 0);
}

/**
 * Immersive live-training display.
 *
 * @param {boolean} enabled
 * @param {{ skipGestureSelector?: string, force?: boolean }} [options]
 *        force: player Live Training — enter fullscreen from any tap (not just
 *        empty canvas) and keep retrying so the browser top bar stays hidden.
 */
export function useLiveFullscreen(enabled = true, options = {}) {
  const tourActive = useTourDemoActive();
  const liveEnabled = enabled && !tourActive;
  const force = Boolean(options.force);
  const skipGestureSelector =
    options.skipGestureSelector || (force ? FORCE_SKIP : DEFAULT_SKIP);

  useEffect(() => {
    if (!liveEnabled) {
      if (tourActive) exitLiveFullscreen();
      return undefined;
    }

    acquireImmersive(force);
    syncVisualViewportHeight();

    const enterFromGesture = () => {
      if (isPingfloTourRunning() || isFullscreenActive()) return;
      requestLiveFullscreen(undefined, { hideBrowserChrome: force });
    };

    const shouldSkipFullscreenGesture = (event) => {
      const target = event?.target;
      if (!target || typeof target.closest !== "function") return false;
      return !!target.closest(skipGestureSelector);
    };

    const onGesture = (event) => {
      if (shouldSkipFullscreenGesture(event)) return;
      enterFromGesture();
    };

    const onFullscreenChange = () => {
      if (force && isFullscreenActive()) pinFullscreenViewport();
      else syncVisualViewportHeight();
      if (!isFullscreenActive()) {
        minimizeMobileBrowserChrome();
      }
    };

    const onViewport = () => {
      if (force && isFullscreenActive()) pinFullscreenViewport();
      else syncVisualViewportHeight();
    };

    const onOrient = () => {
      syncVisualViewportHeight();
      if (!isFullscreenActive()) minimizeMobileBrowserChrome();
    };

    // Click is the gesture mobile Chrome and Safari accept for
    // requestFullscreen. pointerdown is denied, and a touchend request
    // followed by click aborts the first call so the toolbar stays up.
    const gestureOpts = { capture: true };
    document.addEventListener("click", onGesture, gestureOpts);
    document.addEventListener("fullscreenchange", onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", onFullscreenChange);
    window.addEventListener("orientationchange", onOrient);
    window.addEventListener("resize", onViewport);
    window.visualViewport?.addEventListener("resize", onViewport);
    window.visualViewport?.addEventListener("scroll", onViewport);

    return () => {
      document.removeEventListener("click", onGesture, gestureOpts);
      document.removeEventListener("fullscreenchange", onFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", onFullscreenChange);
      window.removeEventListener("orientationchange", onOrient);
      window.removeEventListener("resize", onViewport);
      window.visualViewport?.removeEventListener("resize", onViewport);
      window.visualViewport?.removeEventListener("scroll", onViewport);
      releaseImmersive();
    };
  }, [liveEnabled, skipGestureSelector, force, tourActive]);
}

/** Subscribe to Fullscreen API so player UI can show a tap-to-enter gate. */
export function useIsLiveFullscreen() {
  const [active, setActive] = useState(() =>
    typeof document === "undefined" ? false : isFullscreenActive()
  );
  useEffect(() => {
    const sync = () => setActive(isFullscreenActive());
    sync();
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    document.addEventListener("mozfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
      document.removeEventListener("mozfullscreenchange", sync);
    };
  }, []);
  return active;
}

export default useLiveFullscreen;
